import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Checkbox,
  Tooltip,
  Switch,
  Typography,
  TablePagination,
  TableSortLabel,
  Button,
  IconButton,
  Chip,
  Modal,
  Grid,
  MenuItem,
  TableContainer,
} from '@mui/material';
import * as Yup from 'yup';
import { Formik, Field, Form } from 'formik';
import CustomFormLabel from '../../components/forms/custom-elements/CustomFormLabel';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '@mui/utils';
import { downloadReport } from '../../utils/downloadReport';
import { useNavigate, useLocation } from 'react-router';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CloseOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import CustomTextField from '../forms/custom-elements/CustomTextField';
import CustomSelect from '../forms/custom-elements/CustomSelect';
import {
  approvePartnerJoinAndAdoptCTU,
  getReportsALL,
  getReportsData,
  getSignedUrl,
  partnerJoinAndAdaptCTU,
  downloadEventReport,
} from '../../api';
import { enqueueSnackbar } from 'notistack';
import { getJoinAdoptRequestById, getMediaBySbmIDAndMediaType } from '../../firebase/functions';
import { isApiReportYear } from '../../constants/reportYears';

// Helper function to format numbers with comma separation
const formatNumber = (value) => {
  if (
    typeof value === 'number' ||
    (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '')
  ) {
    const numStr = String(value).trim();
    // Check if it's a mobile number (10 digits, potentially starting with 6-9)
    const isMobileNumber = /^[6-9]\d{9}$/.test(numStr) || numStr.length === 10;

    // Don't format mobile numbers with commas
    if (isMobileNumber) {
      return numStr;
    }

    return Number(value).toLocaleString('en-IN');
  }
  return value;
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const TableComponent = ({
  mainHeaders,
  data,
  updatedPermissions,
  setUpdatedPermissions,
  updateAccess,
  isPublic,
  fileName,
  handleNext,
  handleUpdateAction,
  downloadRow,
  showButton = true,
  handleHGTEdit,
  handleHGTView,
  showPagination = true,
  year, // Add this line to accept the year prop
  hideExportButton,
  serverSidePagination = false,
  currentPage = 0,
  hasMoreData = true,
  // Server-side totals, when the endpoint reports them. Both optional: pages
  // that do not pass them keep the previous "Current Page - N" display and the
  // hasMoreData-driven next button, so nothing else changes.
  totalPages,
  totalCount,
  eventType, // New prop for event type (ctu or cps)
  useNewExportApi = false, // New prop to enable new export functionality
  hideViewEventColumn = false, // New prop to hide view event column
  // Users page only: called with the row when the delete icon is clicked. The
  // page owns the confirmation dialog and the API call, so nothing is deleted
  // from in here. Absent (undefined) for every non-admin caller, which also
  // makes the icon render disabled if the column is ever shown without it.
  onDeleteUser,
  // CTU event list only: called with the row when the delete icon is clicked. Same
  // arrangement as onDeleteUser -- the page owns the confirmation dialog and the API
  // call. Undefined for every non-admin caller, which also renders the icon disabled
  // if the column is ever shown without it.
  onDeleteEvent,
  // Report Event Status only: called with the row when the View button is
  // clicked. The page owns the dialog and the fetch, same arrangement as
  // onDeleteEvent. Undefined everywhere else, which renders the button disabled.
  onViewReport,
  // Keys to render as true/false instead of their raw value. Opt-in per page,
  // deliberately: `event_completed` is a 0/1 flag on the event lists but a COUNT
  // on the campaign dashboard ("Events Completed", next to "Events Created"), so
  // keying off the column name alone would turn a tally of 1 into "true".
  //
  // This affects rendering ONLY. The row data is left untouched, because the
  // Close Event / Update Daily Event / Join-Adopt branches above all test
  // `!row.event_completed`, and the string 'false' is truthy -- rewriting the
  // value would silently invert every one of those.
  booleanColumns = [],
}) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector((state) => state.ProfileReducer);
  const [adoptCTUModal, setAdoptCTUModal] = useState(false);
  const [joinCTUModal, setJoinCTUModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const pageSize = 25;
  const role = useSelector((state) => state.RoleReducer);
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePage = (event, newPage) => {
    if (handleNext) {
      handleNext(newPage);
    }
    setPage(newPage);
  };

  const visibleRows = useMemo(
    () =>
      serverSidePagination || !showPagination
        ? stableSort(data, getComparator(order, orderBy))
        : stableSort(data, getComparator(order, orderBy)).slice(
            page * pageSize,
            page * pageSize + pageSize
          ),
    [order, orderBy, page, pageSize, data, serverSidePagination, showPagination]
  );

  function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };
    const headers = Object.keys(mainHeaders).filter((header) => {
      // Filter out view event columns when hideViewEventColumn is true
      if (hideViewEventColumn && (header === 'view_event' || header === 'view_cps_event')) {
        return false;
      }
      return true;
    });

    // Break long header names on the API-year reports (2025 onwards). Their
    // columns are indicator labels rather than short event fields, so they need
    // wrapping; the 2024 reports keep the single-line layout.
    const isWrappedHeaderYear = isApiReportYear(year);
    const formatHeaderText = (text) => {
      if (isWrappedHeaderYear && text.length > 15) {
        const words = text.split(' ');
        if (words.length > 1) {
          const midPoint = Math.ceil(words.length / 2);
          const firstLine = words.slice(0, midPoint).join(' ');
          const secondLine = words.slice(midPoint).join(' ');
          return (
            <>
              {firstLine}
              <br />
              {secondLine}
            </>
          );
        }
      }
      return text;
    };

    return (
      <TableHead>
        <TableRow>
          {headers?.map((header, index) => {
            return (
              <TableCell
                key={index}
                sx={{
                  fontWeight: 'bold',
                  minWidth: isWrappedHeaderYear ? '120px' : 'auto',
                  whiteSpace: isWrappedHeaderYear ? 'normal' : 'nowrap',
                  lineHeight: isWrappedHeaderYear ? 1.2 : 'normal',
                }}
                sortDirection={orderBy === header ? order : false}
              >
                <TableSortLabel
                  active={orderBy === header}
                  direction={orderBy === header ? order : 'asc'}
                  onClick={createSortHandler(header)}
                >
                  {formatHeaderText(mainHeaders[header])}
                  {orderBy === header ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleEventRedirect = (id) => {
    navigate(`/black-spot-identification`);
  };

  const handleApproveEvent = async (data, status) => {
    setLoading(true);
    try {
      const res = await approvePartnerJoinAndAdoptCTU({
        creator_user_id: data.creator_user_id,
        user_id: data.user_id,
        event_id: data.event_id,
        type: status === 'CREATE' ? 'CREATE' : data.event_type,
        status: status,
      });
      if (res) {
        enqueueSnackbar(`Event ${status?.toLowerCase()} successfully`, {
          autoHideDuration: 6000,
          variant: 'success',
        });
        window.location.reload();
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      enqueueSnackbar(err.data || err.response.data, {
        autoHideDuration: 6000,
        variant: 'error',
      });
    }
  };

  const handleDownloadMedia = async (url, sbmId) => {
    try {
      if (url) {
        const paths = url.split('/').filter(Boolean);
        const folder = paths[0];
        const name = paths[1];
        const signedUrlResponse = await getSignedUrl(folder, name);

        if (signedUrlResponse && signedUrlResponse.status === 200) {
          const signedUrl = signedUrlResponse.data;

          // Handle download for the presigned URL
          const response = await fetch(signedUrl, { method: 'GET' });
          if (!response.ok) {
            throw new Error('Network response was not ok.');
          }
          const blob = await response.blob();
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'sbm_image'; // Adjust filename as needed
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      }
    } catch (error) {
      console.error('Error handling download:', error);
    }
  };

  const handleOpenJoinCTUModal = async (data) => {
    const userId = profile.info.user_record.id;
    if (!userId) return;
    const res = await getJoinAdoptRequestById(userId, data?.event_id);
    if (res.length > 0) {
      enqueueSnackbar('Already Join Request Sent', { variant: 'error' });
    } else {
      setJoinCTUModal(data);
    }
  };

  const handleOpenAdoptCTUModal = async (data) => {
    const userId = profile.info.user_record.id;
    if (!userId) return;
    const res = await getJoinAdoptRequestById(userId, data?.event_id);
    if (res.length > 0) {
      enqueueSnackbar('Already Adopt Request Sent', { variant: 'error' });
    } else {
      setAdoptCTUModal(data);
    }
  };

  const renderTableRows = () => {
    const keysInOrder = Object.keys(mainHeaders).filter((key) => {
      // Filter out view event columns when hideViewEventColumn is true
      if (hideViewEventColumn && (key === 'view_event' || key === 'view_cps_event')) {
        return false;
      }
      return true;
    });
    return visibleRows?.map((row, index) => (
      <TableRow key={index}>
        {keysInOrder?.map((key, rowIndex) => {
          if (key === 'view_event') {
            return (
              <TableCell
                key={rowIndex}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '30px',
                }}
                onClick={() => {
                  // Build URL with theme parameter if available
                  const baseUrl = isPublic
                    ? `/view-event/${row?.event_id}`
                    : `/events/view-event/${row?.event_id}`;
                  const urlWithTheme = row?.event_theme_id
                    ? `${baseUrl}?theme=${row.event_theme_id}`
                    : baseUrl;
                  window.open(urlWithTheme, '_blank');
                }}
              >
                <Button variant="contained" color="primary">
                  View
                  <VisibilityIcon />
                </Button>
              </TableCell>
            );
          }
          if (key === 'view_cps_event') {
            return (
              <TableCell
                key={rowIndex}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '30px',
                }}
                onClick={() => {
                  window.open(`/events/view-cps-event/${row?.event_id || row?.id}`, '_blank');
                }}
              >
                <Button variant="contained" color="primary">
                  View
                  <VisibilityIcon />
                </Button>
              </TableCell>
            );
          }
          if (key === 'view_hgt') {
            return (
              <TableCell key={rowIndex}>
                <IconButton onClick={() => handleHGTView(row)}>
                  <VisibilityIcon />
                </IconButton>
              </TableCell>
            );
          }
          if (key === 'edit_hgt') {
            return (
              <TableCell key={rowIndex}>
                <IconButton onClick={() => handleHGTEdit(row)}>
                  <EditOutlinedIcon />
                </IconButton>
              </TableCell>
            );
          }
          if (key === 'approve_action') {
            return (
              <TableCell key={rowIndex}>
                {profile?.info?.user_details &&
                (profile?.info?.user_details[0]?.role_id == 12 ||
                  profile?.info?.user_details[0]?.role_id == 1) ? (
                  <Switch
                    checked={row?.isPartnerUserApproved}
                    onChange={(e) => {
                      handleUpdateAction(row.user_id);
                    }}
                  />
                ) : (
                  '-'
                )}
              </TableCell>
            );
          }
          if (key === 'edit_ctu') {
            return (
              <TableCell key={rowIndex}>
                <IconButton onClick={() => handleEventRedirect(row.doc_id)}>
                  <EditOutlinedIcon />
                </IconButton>
              </TableCell>
            );
          }
          if (key === 'view_report') {
            return (
              <TableCell key={rowIndex}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!onViewReport}
                  onClick={() => onViewReport && onViewReport(row)}
                >
                  View
                  <VisibilityIcon />
                </Button>
              </TableCell>
            );
          }
          if (key === 'delete_event') {
            return (
              <TableCell key={rowIndex}>
                <IconButton
                  aria-label={`Delete ${row?.event_user_name || 'event'}`}
                  color="error"
                  disabled={!onDeleteEvent}
                  onClick={() => onDeleteEvent && onDeleteEvent(row)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </TableCell>
            );
          }
          if (key === 'delete_user') {
            return (
              <TableCell key={rowIndex}>
                <IconButton
                  aria-label={`Delete ${row?.name || 'user'}`}
                  color="error"
                  disabled={!onDeleteUser}
                  onClick={() => onDeleteUser && onDeleteUser(row)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </TableCell>
            );
          }
          if (key === 'access') {
            const values = Object.keys(row[key]);
            return (
              <TableCell key={rowIndex}>
                {values?.map((o) => (
                  <Box key={o} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <CustomFormLabel>{t(o)}</CustomFormLabel>
                    <Tooltip title={o}>
                      <Checkbox name={o} checked={row[key][o]} />
                    </Tooltip>
                  </Box>
                ))}
              </TableCell>
            );
          }
          if (key === 'action') {
            const updateRow = updatedPermissions?.find((o) => o.path === row.path);
            return (
              <TableCell key={rowIndex}>
                {updateAccess.map((obj, ind) => (
                  <Box
                    key={ind}
                    display={'flex'}
                    alignItems={'center'}
                    onClick={() => {
                      setUpdatedPermissions((prevState) =>
                        prevState.map((item) =>
                          item.path === row.path
                            ? {
                                ...item,
                                access: {
                                  ...item.access,
                                  [obj]: !item.access[obj],
                                },
                              }
                            : item
                        )
                      );
                    }}
                  >
                    <Typography variant="h5">{t(obj)}</Typography>
                    <Checkbox name={obj} checked={updateRow?.access[obj]} />
                  </Box>
                ))}
                <Typography variant="h5">Active</Typography>
                <Switch
                  checked={updateRow?.active}
                  onChange={(e) => {
                    setUpdatedPermissions((prevComponents) =>
                      prevComponents.map((item) =>
                        item.path === row.path ? { ...item, active: e.target.checked } : item
                      )
                    );
                  }}
                />
              </TableCell>
            );
          }
          if (key === 'copy_link') {
            return (
              <TableCell key={rowIndex}>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/public-events/${row.doc_id}`
                    );
                  }}
                  variant="contained"
                  color="primary"
                >
                  Copy Link
                </Button>
              </TableCell>
            );
          }
          if (key === 'ctu_status') {
            return (
              <TableCell key={rowIndex}>
                <Chip
                  label={row[key]}
                  variant="contained"
                  sx={{ color: row[key] === 'Pending' ? 'red' : 'green' }}
                />
              </TableCell>
            );
          }
          if (key === 'add_event') {
            return (
              <TableCell key={rowIndex}>
                <Button onClick={() => handleCloseEvent(row)} variant="contained" color="primary">
                  Add Event
                </Button>
              </TableCell>
            );
          }
          if (key === 'images') {
            return (
              <TableCell key={rowIndex}>
                {row[key]?.map((obj, index) => (
                  <Button
                    onClick={() => handleDownloadMedia(row[key][index], data[index]?.doc_id)}
                    variant="contained"
                    color="primary"
                    sx={{ marginLeft: '20px' }}
                  >
                    {`Download Media ${index + 1}`}
                  </Button>
                ))}
              </TableCell>
            );
          }
          if (key === 'approve_event') {
            return (
              <TableCell key={rowIndex}>
                <Button variant="contained" color="primary">
                  Approve Event
                </Button>
              </TableCell>
            );
          }
          if (key === 'title') {
            return (
              <TableCell
                key={rowIndex}
                style={{
                  maxWidth: '200px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row[key]}
              </TableCell>
            );
          }

          if (key === 'impact') {
            return (
              <TableCell
                key={rowIndex}
                style={{
                  maxWidth: '200px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row[key]}
              </TableCell>
            );
          }

          if (key === 'story') {
            return (
              <TableCell
                key={rowIndex}
                style={{
                  maxWidth: '200px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row[key]}
              </TableCell>
            );
          }

          if (key === 'adopt_event') {
            return (
              <TableCell key={rowIndex} sx={{ gap: '20px' }}>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  color="primary"
                  onClick={() => handleApproveEvent(row, 'APPROVED')}
                >
                  Approve Event
                </LoadingButton>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  color="primary"
                  sx={{ backgroundColor: 'red', marginLeft: '20px' }}
                  onClick={() => handleApproveEvent(row, 'REJECTED')}
                >
                  Reject Event
                </LoadingButton>
              </TableCell>
            );
          }
          if (key === 'approve_partner_event') {
            if (
              profile?.info?.user_details &&
              row.district_code == profile?.info?.user_details[0]?.district_code
            ) {
              return (
                <TableCell key={rowIndex} sx={{ gap: '20px' }}>
                  <LoadingButton
                    loading={loading}
                    variant="contained"
                    color="primary"
                    onClick={() => handleApproveEvent(row, 'CREATE')}
                  >
                    Approve Event
                  </LoadingButton>
                </TableCell>
              );
            }
            return <TableCell>-</TableCell>;
          }
          if (key === 'event_action') {
            // `user_record` is the users-table row, where the id column is `id`.
            // `user_id` is the name the OTHER tables use for that same value
            // (user_details.user_id, events.creator_user_id) -- it is not a
            // column on users. These gates used to read user_record.user_id,
            // which worked only because the old userCreation wrote a redundant
            // copy of `id` onto the users row as well. userCreationV2 stopped
            // writing that copy on 2026-08-17, so every user created since had
            // undefined on the left of this compare and saw Edit/Close/Daily
            // update permanently disabled on their own open events -- 3,968
            // users, 6.1% of the table. Read `id` and both cohorts work:
            // user_id === id on every row that still carries both.
            if (
              (profile?.info?.user_record?.id === row?.creator_user_id && !row.event_completed) ||
              (profile?.info?.user_record?.id === row?.user_id &&
                row?.data_source === 'MAP_MY_INDIA' &&
                !row.event_completed)
            ) {
              if (row.event_type === 'PARTNER_ADOPT_CTU') {
                return (
                  <TableCell key={rowIndex}>
                    <Button variant="contained" color="primary" disabled>
                      Edit <EditOutlinedIcon />
                    </Button>
                  </TableCell>
                );
              } else {
                return (
                  <TableCell
                    key={rowIndex}
                    onClick={() => navigate(`/events/edit-event/${row?.event_id}`, { state: row })}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Button variant="contained" color="primary">
                      Edit <EditOutlinedIcon />
                    </Button>
                  </TableCell>
                );
              }
            }
            return (
              <TableCell key={rowIndex}>
                <Button variant="contained" color="primary" disabled>
                  Edit <EditOutlinedIcon />
                </Button>
              </TableCell>
            );
          }
          if (key === 'event_cps_action') {
            if (
              (profile?.info?.user_record?.id === row?.creator_user_id && !row.event_completed) ||
              (profile?.info?.user_record?.id === row?.user_id && !row.event_completed)
            ) {
              return (
                <TableCell
                  key={rowIndex}
                  onClick={() => {
                    console.log('CPS Edit clicked for row:', row);
                    const eventId = row?.event_id || row?.doc_id || row?.id;
                    console.log('Navigating to:', `/events/edit-cps-event/${eventId}`);
                    navigate(`/events/edit-cps-event/${eventId}`, { state: row });
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <Button variant="contained" color="primary">
                    Edit <EditOutlinedIcon />
                  </Button>
                </TableCell>
              );
            }
            return (
              <TableCell key={rowIndex}>
                <Button variant="contained" color="primary" disabled>
                  Edit <EditOutlinedIcon />
                </Button>
              </TableCell>
            );
          }
          if (key === 'close_event') {
            if (
              (profile?.info?.user_record?.id === row?.creator_user_id && !row.event_completed) ||
              (profile?.info?.user_record?.id === row?.user_id &&
                row?.data_source === 'MAP_MY_INDIA' &&
                !row.event_completed)
            ) {
              if (row.event_type === 'PARTNER_ADOPT_CTU') {
                <TableCell key={rowIndex}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ backgroundColor: 'red' }}
                    disabled
                  >
                    Close <CloseOutlined />
                  </Button>
                </TableCell>;
              } else {
                return (
                  <TableCell
                    key={rowIndex}
                    onClick={() => navigate(`/events/close-event/${row?.event_id}`, { state: row })}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Button variant="contained" color="primary" sx={{ backgroundColor: 'red' }}>
                      Close <CloseOutlined />
                    </Button>
                  </TableCell>
                );
              }
            }
            if (
              profile?.info?.user_details &&
              profile?.info?.user_details[0]?.role_id === 10 &&
              profile?.info?.user_record?.id === row?.user_id &&
              !row.event_completed &&
              row?.event_type?.toUpperCase() === 'PARTNER_ADOPT_CTU' &&
              row?.status?.toUpperCase() === 'APPROVED'
            ) {
              return (
                <TableCell
                  key={rowIndex}
                  onClick={() => navigate(`/events/close-event/${row?.event_id}`, { state: row })}
                  sx={{ cursor: 'pointer' }}
                >
                  <Button variant="contained" color="primary" sx={{ backgroundColor: 'red' }}>
                    Close <CloseOutlined />
                  </Button>
                </TableCell>
              );
            }
            return (
              <TableCell key={rowIndex}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ backgroundColor: 'red' }}
                  disabled
                >
                  Close <CloseOutlined />
                </Button>
              </TableCell>
            );
          }
          if (key === 'close_cps_event') {
            if (
              (profile?.info?.user_record?.id === row?.creator_user_id && !row.event_completed) ||
              (profile?.info?.user_record?.id === row?.user_id && !row.event_completed)
            ) {
              return (
                <TableCell
                  key={rowIndex}
                  onClick={() => {
                    console.log('CPS Close clicked for row:', row);
                    const eventId = row?.event_id || row?.doc_id || row?.id;
                    console.log('Navigating to:', `/events/close-cps-event/${eventId}`);
                    navigate(`/events/close-cps-event/${eventId}`, { state: row });
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <Button variant="contained" color="primary" sx={{ backgroundColor: 'red' }}>
                    Close <CloseOutlined />
                  </Button>
                </TableCell>
              );
            }

            return (
              <TableCell key={rowIndex}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ backgroundColor: 'red' }}
                  disabled
                >
                  Close <CloseOutlined />
                </Button>
              </TableCell>
            );
          }
          if (key === 'update_daily_event') {
            if (
              (profile?.info?.user_record?.id === row?.creator_user_id && !row.event_completed) ||
              (profile?.info?.user_record?.id === row?.user_id &&
                row?.data_source === 'MAP_MY_INDIA' &&
                !row.event_completed)
            ) {
              if (row.event_name_id === 34) {
                if (row.event_type === 'PARTNER_ADOPT_CTU') {
                  return (
                    <TableCell key={rowIndex}>
                      <Button variant="contained" color="primary" disabled>
                        Daily update
                      </Button>
                    </TableCell>
                  );
                } else {
                  return (
                    <TableCell
                      key={rowIndex}
                      onClick={() =>
                        navigate(`/events/update-daily-event/${row?.event_id}`, { state: row })
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      <Button variant="contained" color="primary">
                        Daily update
                      </Button>
                    </TableCell>
                  );
                }
              } else {
                return (
                  <TableCell key={rowIndex}>
                    <Button variant="contained" color="primary" disabled>
                      Daily update
                    </Button>
                  </TableCell>
                );
              }
            }
            if (
              profile?.info?.user_details &&
              profile?.info?.user_details[0]?.role_id === 10 &&
              row.event_name_id === 34 &&
              profile?.info?.user_record?.id === row?.user_id &&
              !row.event_completed &&
              row.event_type?.toUpperCase() === 'PARTNER_ADOPT_CTU' &&
              row.status?.toUpperCase() === 'APPROVED'
            ) {
              return (
                <TableCell
                  key={rowIndex}
                  onClick={() =>
                    navigate(`/events/update-daily-event/${row?.event_id}`, { state: row })
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <Button variant="contained" color="primary">
                    Daily update
                  </Button>
                </TableCell>
              );
            }
            return (
              <TableCell key={rowIndex}>
                <Button variant="contained" color="primary" disabled>
                  Daily update
                </Button>
              </TableCell>
            );
          }
          if (key === 'before_image') {
            return (
              <TableCell key={rowIndex} sx={{ minWidth: '120px', textAlign: 'center' }}>
                {row[key] ? (
                  <button
                    type="button"
                    onClick={() => window.open(row[key], '_blank')}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                    aria-label="View before image in full size"
                  >
                    <img
                      src={row[key]}
                      alt="Before"
                      style={{
                        width: '80px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        display: 'block',
                      }}
                    />
                  </button>
                ) : (
                  '-'
                )}
              </TableCell>
            );
          }
          if (key === 'after_image') {
            return (
              <TableCell key={rowIndex} sx={{ minWidth: '120px', textAlign: 'center' }}>
                {row[key] ? (
                  <button
                    type="button"
                    onClick={() => window.open(row[key], '_blank')}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                    aria-label="View after image in full size"
                  >
                    <img
                      src={row[key]}
                      alt="After"
                      style={{
                        width: '80px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        display: 'block',
                      }}
                    />
                  </button>
                ) : (
                  '-'
                )}
              </TableCell>
            );
          }
          if (key === 'ctu_action' && !row.event_completed) {
            if (row.event_name_id == 34 && !row.event_type) {
              return (
                <TableCell
                  key={rowIndex}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '30px',
                  }}
                >
                  <Button
                    onClick={() => handleOpenJoinCTUModal(row)}
                    variant="contained"
                    color="primary"
                  >
                    Join CTU
                  </Button>
                  <Button
                    onClick={() => handleOpenAdoptCTUModal(row)}
                    variant="contained"
                    color="primary"
                  >
                    Adopt CTU
                  </Button>
                </TableCell>
              );
            }
            if (row.event_type === 'PARTNER_ADOPT_CTU') {
              return <TableCell>{row.status === 'APPROVED' ? '-' : ' Adopt requested'}</TableCell>;
            }
            if (row.event_type === 'PARTNER_JOIN_CTU') {
              return <TableCell>{row.status === 'APPROVED' ? '-' : 'Join requested'}</TableCell>;
            }
            return <TableCell key={rowIndex}>-</TableCell>;
          }
          // Declared boolean columns. The same flag arrives in three shapes
          // depending on the endpoint: 0/1 from ClickHouse (getAllEventsByQuery),
          // a real boolean from the DynamoDB paths, and occasionally the strings
          // '0'/'1'. Normalise all of them rather than trusting one source.
          if (booleanColumns.includes(key)) {
            const raw = row[key];
            const isTrue = raw === true || raw === 1 || raw === '1' || raw === 'true';
            return <TableCell key={rowIndex}>{isTrue ? 'True' : 'False'}</TableCell>;
          }
          return (
            <TableCell
              key={rowIndex}
              style={{ fontWeight: typeof row[key] === 'boolean' ? '700' : '400' }}
            >
              {Array.isArray(row[key])
                ? row[key].join(', ')
                : typeof row[key] === 'boolean'
                  ? row[key]
                    ? 'YES'
                    : 'NO'
                  : formatNumber(row[key]) || '-'}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  const handleAdoptCTU = async (values) => {
    setLoading(true);
    let data = adoptCTUModal;
    if (!data) {
      data = joinCTUModal;
    }
    try {
      const res = await partnerJoinAndAdaptCTU({
        ...values,
        event_id: data.doc_id,
        event_type: adoptCTUModal ? 'PARTNER_ADOPT_CTU' : 'PARTNER_JOIN_CTU',
      });
      if (res) {
        setAdoptCTUModal(undefined);
        setJoinCTUModal(undefined);
        enqueueSnackbar('Request submitted successfully', {
          variant: 'success',
          autoHideDuration: 6000,
        });
        window.location.reload();
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      return enqueueSnackbar(err?.response?.data?.error, {
        variant: 'error',
        autoHideDuration: 6000,
      });
    }
  };

  const initialValues = {
    ctu_site_partner_coordinator_name: '',
    ctu_site_partner_coordinator_phone_number: '',
    type_of_adoption_model: '',
  };

  const formikValidationSchema = Yup.object().shape({
    ctu_site_partner_coordinator_name: Yup.string().required('Field is required'),
    ctu_site_partner_coordinator_phone_number: Yup.string()
      .matches(/^[1-9]\d{9}$/, 'Number must be exactly 10 digits and cannot start with 0')
      .required('Field is required'),
    type_of_adoption_model: Yup.string().required('Field is required'),
  });

  const handleDownload = (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    enqueueSnackbar('List Downloaded Successfully', { variant: 'success' });
  };

  const handleDownloadReport = async (file) => {
    let res;
    if (file?.toLowerCase() === 'events') {
      res = await getReportsData({
        resource: 'events',
        fields: `[${Object.keys(downloadRow)
          .map((key) => `"${key}"`)
          .join(',')}]`,
        rank_by: role.info?.main_key,
        filters: `["${role.info?.main_key}='${
          profile?.info?.user_details[0][role?.info?.main_key]
        }'"]`,
      });
    }
    if (file?.toLowerCase() === 'users') {
      res = await getReportsData({
        resource: 'user_details',
        fields: `[${Object.keys(downloadRow)
          .map((key) => `"${key}"`)
          .join(',')}]`,
        rank_by: role.info?.main_key,
        filters: `["${role.info?.main_key}='${
          profile?.info?.user_details[0][role?.info?.main_key]
        }'"]`,
      });
    }

    if (res?.data?.eventsFileUrl) {
      // Initiating download
      const link = document.createElement('a');
      link.href = res.data.eventsFileUrl;
      link.setAttribute('download', fileName); // Set the file name if available
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      {fileName && showButton && !hideExportButton && (
        <LoadingButton
          onClick={async () => {
            setIsLoading(true);

            // Handle new export API for CTU and CPS events
            if (useNewExportApi && eventType && profile?.info?.user_details?.[0]?.user_id) {
              try {
                const userId = profile.info.user_details[0].user_id;
                const response = await downloadEventReport(eventType, userId);

                // API returns XLSX file directly
                enqueueSnackbar('Report downloaded successfully', { variant: 'success' });
                setIsLoading(false);
                return;
              } catch (error) {
                console.error('Export failed:', error);
                enqueueSnackbar('Export failed. Please try again.', { variant: 'error' });
                setIsLoading(false);
                return;
              }
            }

            // Original export logic
            // if (profile?.info?.user_details[0]?.role_id === 1) {
            //   if (fileName?.toLowerCase() === 'events') {
            //     const res = await getReportsALL('events');
            //     if (res.data) {
            //       handleDownload(res.data.fileUrl, 'EVENTS');
            //     }
            //   } else if (fileName?.toLowerCase() === 'users') {
            //     const res = await getReportsALL('user_details');
            //     if (res.data) {
            //       handleDownload(res.data.fileUrl, 'USERS');
            //     }
            //   }
            //   downloadReport(data, mainHeaders, fileName);
            //   enqueueSnackbar('List Downloaded Successfully', { variant: 'success' });
            //   setIsLoading(false);
            //   return;
            // }
            if (fileName?.toLowerCase() === 'events') {
              await handleDownloadReport('events');
              enqueueSnackbar('List Downloaded Successfully', { variant: 'success' });
              setIsLoading(false);
              return;
            } else if (fileName?.toLowerCase() === 'users') {
              await handleDownloadReport('users');
              enqueueSnackbar('List Downloaded Successfully', { variant: 'success' });
              setIsLoading(false);
              return;
            }

            downloadReport(data, mainHeaders, fileName);
            enqueueSnackbar('List Downloaded Successfully', { variant: 'success' });
            setIsLoading(false);
          }}
          variant="contained"
          color="primary"
          loading={isLoading}
        >
          Export All Data
        </LoadingButton>
      )}
      <TableContainer sx={{ overflow: 'auto' }}>
        <Table
          stickyHeader
          aria-label="custom pagination table"
          sx={{
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={data.length}
          />
          <TableBody>{renderTableRows()}</TableBody>
        </Table>
      </TableContainer>
      {showPagination &&
        (() => {
          // Zero-based page index, whichever pagination mode is in play.
          const pageIndex = serverSidePagination ? currentPage : page;

          // Prefer the server's page count. Client-side paging can work it out
          // from the rows it holds; server-side paging cannot, which is why
          // totalPages has to come from the caller.
          const resolvedTotalPages = Number.isFinite(totalPages)
            ? totalPages
            : serverSidePagination
              ? undefined
              : Math.ceil(data.length / pageSize) || 1;

          // With a known page count, the last page is knowable exactly. Without
          // one, fall back to hasMoreData as before -- pages that report neither
          // default hasMoreData to true, which is why Next used to stay live past
          // the end of the data.
          const isLastPage = resolvedTotalPages
            ? pageIndex + 1 >= resolvedTotalPages
            : serverSidePagination
              ? !hasMoreData
              : false;

          return (
            <Box
              sx={{
                position: 'sticky', // Keep pagination sticky
                bottom: 0,
                right: 0,
                backgroundColor: 'white', // Ensure it's readable against background
                zIndex: 1000, // Stay on top of other elements
                display: 'flex',
                justifyContent: 'flex-end', // Align to right
                padding: '16px',
                borderTop: '1px solid #ddd', // Optional border for separation
              }}
            >
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                // A real count lets MUI disable Next on the last page by itself. -1
                // is its "total unknown" mode, which is what every caller that does
                // not report a total still gets.
                count={Number.isFinite(totalCount) ? totalCount : -1}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={handleChangePage}
                onRowsPerPageChange={() => {}}
                labelDisplayedRows={() => null} // Shown in the summary box instead
                labelRowsPerPage=""
                nextIconButtonProps={{
                  style: { color: isLastPage ? 'lightgrey' : 'black' },
                  disabled: isLastPage,
                }}
                backIconButtonProps={{
                  style: { color: pageIndex === 0 ? 'lightgrey' : 'black' },
                  disabled: pageIndex === 0,
                }}
              />
              {/* Page summary. Falls back to the page number alone when the caller
              reports no totals, which is what it always showed before. */}
              <Box sx={{ position: 'absolute', right: '100px', top: '30px' }}>
                {resolvedTotalPages
                  ? `Page ${pageIndex + 1} of ${resolvedTotalPages}`
                  : `Current Page - ${pageIndex + 1}`}
                {Number.isFinite(totalCount)
                  ? ` · ${totalCount.toLocaleString('en-IN')} records`
                  : ''}
              </Box>
            </Box>
          );
        })()}
      {(adoptCTUModal || joinCTUModal) && (
        <Modal
          open={true}
          onClose={() => {
            setAdoptCTUModal(undefined);
            setJoinCTUModal(undefined);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: window.innerWidth > 767 ? '50%' : '100%',
              bgcolor: 'background.paper',
              p: 4,
              minHeight: window.innerWidth > 767 ? 'auto' : '100%',
              maxHeight: window.innerWidth > 767 ? '95%' : '100%',
              overflow: 'auto',
              textAlign: 'center',
            }}
          >
            <Box
              onClick={() => {
                setAdoptCTUModal(undefined);
                setJoinCTUModal(undefined);
              }}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <CloseIcon />
            </Box>
            <Typography variant="h2" sx={{ textAlign: 'center', marginBottom: '20px' }}>
              {adoptCTUModal ? 'Adopt CTU' : 'Join CTU'}
            </Typography>
            <Box sx={{ textAlign: 'left' }}>
              <Formik
                initialValues={initialValues}
                validationSchema={formikValidationSchema}
                onSubmit={handleAdoptCTU}
              >
                {({ errors, touched, handleChange, values, setFieldValue }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        width: '100%',
                        gap: '12px',
                        marginTop: '20px',
                      }}
                    >
                      <Grid container spacing={2} mb={2}>
                        <Grid item xs={12}>
                          <CustomFormLabel>CTU site Partner coordinator name*</CustomFormLabel>
                          <Field
                            as={CustomTextField}
                            id="ctu_site_partner_coordinator_name"
                            name="ctu_site_partner_coordinator_name"
                            variant="outlined"
                            fullWidth
                            autoComplete="off"
                          />
                          {errors.ctu_site_partner_coordinator_name &&
                          touched.ctu_site_partner_coordinator_name ? (
                            <div className="error-message">
                              {errors.ctu_site_partner_coordinator_name}
                            </div>
                          ) : null}
                        </Grid>
                        <Grid item xs={12}>
                          <CustomFormLabel>
                            CTU site Partner coordinator phone number*
                          </CustomFormLabel>
                          <Field
                            as={CustomTextField}
                            id="ctu_site_partner_coordinator_phone_number"
                            name="ctu_site_partner_coordinator_phone_number"
                            variant="outlined"
                            type="number"
                            fullWidth
                            autoComplete="off"
                            inputProps={{
                              maxLength: 10,
                              onInput: (e) => {
                                if (e.target.value.length > 10) {
                                  e.target.value = e.target.value.slice(0, 10);
                                }
                              },
                            }}
                          />
                          {errors.ctu_site_partner_coordinator_phone_number &&
                          touched.ctu_site_partner_coordinator_phone_number ? (
                            <div className="error-message">
                              {errors.ctu_site_partner_coordinator_phone_number}
                            </div>
                          ) : null}
                        </Grid>
                        <Grid item xs={12}>
                          <CustomFormLabel>Types of adoption model*</CustomFormLabel>
                          <Field
                            as={CustomSelect}
                            id="type_of_adoption_model"
                            name="type_of_adoption_model"
                            variant="outlined"
                            fullWidth
                            labelId="type_of_adoption_model"
                            displayEmpty
                            value={values.type_of_adoption_model}
                            onChange={handleChange}
                          >
                            <MenuItem value="one_time_clearance_support">
                              One time clearance support for CTU site
                            </MenuItem>
                            <MenuItem value="one_time_clearance_maintenance">
                              One time clearance of CTU site & maintenance
                            </MenuItem>
                            <MenuItem value="only_maintenance_beautification">
                              Only Maintenance, & Beautification of reclaimed CTU site
                            </MenuItem>
                            <MenuItem value="any_other">Any other</MenuItem>
                          </Field>
                          {errors.type_of_adoption_model && touched.type_of_adoption_model ? (
                            <div className="error-message">{errors.type_of_adoption_model}</div>
                          ) : null}
                        </Grid>
                      </Grid>

                      <LoadingButton
                        sx={{
                          alignSelf: 'center',
                          minWidth: '240px',
                          marginTop: '30px',
                          padding: '15px 20px',
                        }}
                        color="primary"
                        variant="contained"
                        type="submit"
                        loading={loading}
                      >
                        Save
                      </LoadingButton>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default TableComponent;
